import React, { useState } from 'react';
import { captureException } from '../../lib/telemetry/posthog/frontend';

/** Shared respondent branding and optional progress or call controls. */
export interface RespondentHeaderProps {
  /** Undefined reserves space while identity resolves; null uses the holito mark when allowed. */
  workspaceLogoUrl?: string | null;
  targetName?: string;
  /**
   * Whether the holito mark may stand in for a missing or failed workspace logo.
   * False for organizations entitled to remove holito branding that turned it off.
   */
  holitoBranding?: boolean;
  children?: React.ReactNode;
}

/** Keep the same header geometry through admission, loading and participation. */
export function RespondentHeader({ workspaceLogoUrl, targetName, holitoBranding = true, children }: RespondentHeaderProps) {
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const showWorkspaceLogo = !!workspaceLogoUrl && workspaceLogoUrl !== failedLogoUrl;
  const showHolitoMark = !showWorkspaceLogo && workspaceLogoUrl !== undefined && holitoBranding;
  return <header className="p-4 border-b border-border shrink-0 z-10 backdrop-blur-sm bg-background/90">
    <div className="w-full flex items-center justify-between gap-2">
      <div className="flex items-center gap-3 min-w-0 h-6 md:h-8">
        {showWorkspaceLogo ? <img
          src={workspaceLogoUrl}
          alt={targetName ? `${targetName} logo` : 'Workspace logo'}
          className="h-6 md:h-8 w-auto max-w-[30vw] md:max-w-[240px] object-contain"
          draggable={false}
          onError={() => {
            setFailedLogoUrl(workspaceLogoUrl!);
            captureException(new Error('Workspace logo could not load'), 'RespondentHeader.logo');
          }}
        /> : showHolitoMark ? <a
          href="https://holito.app/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <img src="/holito-logo-black.svg" alt="holito" className="h-6 md:h-8 w-auto dark:invert" />
          <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-wash-accent text-foreground rounded-lg">Beta</span>
        </a> : null}
      </div>
      {children}
    </div>
  </header>;
}
